<template>
  <form @submit="onSubmit">
    <div class="flex flex-col gap-2 p-2">
      <Fieldset legend="Payee Info" class="my-3">
        <div class="flex flex-col">
          <div class="flex flex-col items-center gap-2">
            <RadioButtonGroup v-model="payeeOption" name="payeeOption" :options="payeeOptions" :onChange="payeeOptionSelected" />
            <StackedLabelInput name="payeeName" label="Payee" size="lg:w-2/3" :disabled="payeeOption !== 'Other'" classes="required" />
          </div>
        </div>
      </Fieldset>
      <Fieldset legend="Bank Info" class="my-3">
        <div class="flex flex-col">
          <div class="flex flex-col items-center gap-2">
            <StackedLabelInput name="bankName" label="Bank Name" size="w-full" />
            <div class="flex flex-row w-full gap-2">
              <StackedLabelInput name="bankNumber" label="Bank Number" size="lg:w-1/2" />
              <StackedLabelInput name="bankAccount" label="Account Number" size="lg:w-1/2" />
            </div>
            <div class="flex flex-row w-full gap-2">
              <StackedLabelInput name="bankCity" label="Bank City" size="lg:w-1/2" />
              <StackedLabelInput name="bankABANumber" label="Bank ABA Number" size="lg:w-1/2" />
            </div>
          </div>
        </div>
      </Fieldset>
    </div>
    <slot name="navigation"> </slot>
  </form>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { type Entity } from "@/types/Entity";
import { z } from "zod";

enum PayeeOption {
  NAME = "Use Registered Name",
  COMPANY = "Use Company Name",
  OTHER = "Other",
}

const defaultPayee = (): void => {
  setFieldValue("payeeName", props.data.firstName + " " + props.data.lastName);
};

defineExpose({ defaultPayee });

const emit = defineEmits<{
  submitStep: [values: any];
}>();

interface Props {
  entity?: Entity;
  data: any;
  setValues?: boolean;
  mode: "Add" | "Edit" | "Copy";
}
const props = withDefaults(defineProps<Props>(), {
  setValues: false,
});

const payeeOption = ref("");
const payeeOptions = ["Use Registered Name", "Use Company Name", "Other"];

const initialValues: any = {};

if (props.entity) {
  initialValues.bankNumber = props.entity.bankNumber;
  initialValues.bankAccount = props.entity.bankAccount;
  initialValues.bankName = props.entity.bankName;
  initialValues.bankCity = props.entity.bankCity;
  initialValues.bankABANumber = props.entity.bankABANumber;
}

const { handleSubmit, setFieldValue } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      payeeName: z.string().min(1, { message: "Payee Name is required" }),
      bankNumber: z.string().default(""),
      bankAccount: z.string().default(""),
      bankABANumber: z.string().default(""),
      bankName: z.string().default(""),
      bankCity: z.string().default(""),
    })
  ),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

onMounted(async (): Promise<void> => {
  let currentPayee = props.setValues ? props.data.payeeName : props.entity ? props.entity.payee : undefined;
  const fullName = props.data.firstName + " " + props.data.lastName;

  if (currentPayee) {
    setFieldValue("payeeName", currentPayee);

    if (currentPayee === fullName) {
      payeeOption.value = PayeeOption.NAME;
    } else if (currentPayee === props.data.companyName) {
      payeeOption.value = PayeeOption.COMPANY;
    } else {
      payeeOption.value = PayeeOption.OTHER;
    }
  } else if (fullName.length > 0) {
    payeeOption.value = PayeeOption.NAME;
    setFieldValue("payeeName", fullName);
  } else if (props.data.companyName.length > 0) {
    payeeOption.value = PayeeOption.COMPANY;
    setFieldValue("payeeName", props.data.companyName);
  } else {
    payeeOption.value = PayeeOption.OTHER;
  }
});

if (props.setValues) {
  setFieldValue("bankNumber", props.data.bankNumber);
  setFieldValue("bankAccount", props.data.bankAccount);
  setFieldValue("bankABANumber", props.data.bankABANumber);
  setFieldValue("bankName", props.data.bankName);
  setFieldValue("bankCity", props.data.bankCity);
}

const payeeOptionSelected = (): void => {
  if (payeeOption.value === PayeeOption.NAME) {
    setFieldValue("payeeName", props.data.firstName + " " + props.data.lastName);
  } else if (payeeOption.value === PayeeOption.COMPANY) {
    setFieldValue("payeeName", props.data.companyName);
  }
};

const onSubmit = handleSubmit((values: any): void => {
  emit("submitStep", values);
});
</script>
